/**
 * Per-event property definitions, from the business sheet
 * (Events_Master + Event_Properties), updated to docs/moengage_events_handover.md.
 *
 * - `stage`     goes on every event as the `stage` common property (sheet value, verbatim).
 * - `pageName`  default for `page_name` when the event lists it (call sites may override).
 * - `required` / `optional` map property → type. Missing / wrong-typed props
 *   only produce a console warning in debug builds; nothing ever throws.
 * - `enums`     allowed values for a property.
 *
 * Common properties (lead_id, session_id, channel, platform, stage) are added
 * by commonProps.js and are not repeated here.
 */
import { EVENTS } from './events';

const S = 'string';
const N = 'number';

/** page_name + email_id + mobile_number — the most common property set in the sheet. */
const PEM = Object.freeze({ page_name: S, email_id: S, mobile_number: S });
const BANK_OPTIONAL = Object.freeze({ bank_ifsc: S, bank_name_match_score: N });
const BANK_REQUIRED = Object.freeze({ ...PEM, bank_verification_mode: S });
const BANK_ENUMS = Object.freeze({ bank_verification_mode: ['Prefill', 'PD', 'RPD'] });
const ESIGN_REQUIRED = Object.freeze({ ...PEM, esign_provider: S, esign_txn_id: S });
const OTP_REQUIRED = Object.freeze({ otp_channel: S, otp_result: S });

const def = (stage, pageName, required = {}, optional = {}, enums = {}) => Object.freeze({
    stage,
    pageName,
    required: Object.freeze({ ...required }),
    optional: Object.freeze({ ...optional }),
    enums: Object.freeze({ ...enums }),
});

const E = EVENTS;

export const EVENT_SCHEMA = Object.freeze({
    // 1–2 REGISTRATION
    [E.REGISTRATION.PAGE_LOADED]: def('REGISTRATION', 'registration', { page_name: S }),
    [E.REGISTRATION.PROCEED]: def('REGISTRATION', 'registration', { mobile_number: S }),
    // Handover keys here are `mobile` / `name`, not mobile_number.
    [E.REGISTRATION.LEAD_FILLED_CDP]: def('REGISTRATION', undefined, { mobile: S, name: S, ekyc_user_id: S }),

    // 3–4 OTP Verification
    [E.OTP.VERIFIED_SUCCESS]: def('OTP Verification', 'mobile_otp',
        { mobile_number: S, ...OTP_REQUIRED }, { otp_attempt_no: N }),
    [E.OTP.LEAD_VERIFIED]: def('OTP Verification', 'mobile_otp', { page_name: S, mobile_number: S }),
    [E.OTP.LEAD_VERIFIED_CDP]: def('OTP Verification', undefined, { mobile_number: S, ekyc_user_id: S }),

    // 5–8 EMAIL
    [E.EMAIL.GOOGLE_SIGNIN_INITIATED]: def('EMAIL', 'verify_email', PEM, { google_sub: S }),
    [E.EMAIL.GOOGLE_SIGNIN_SUCCESS]: def('EMAIL', 'verify_email', PEM, { google_sub: S }),
    [E.EMAIL.MANUALLY_SUBMITTED]: def('EMAIL', 'verify_email', PEM),
    // otp_* are optional: the Google "already verified" path sends this event without an OTP.
    [E.EMAIL.OTP_VERIFIED_SUCCESS]: def('EMAIL', 'verify_email',
        PEM, { ...OTP_REQUIRED, otp_attempt_no: N, signin_with_google: S }),

    // 9–10 PAN_STAGE
    [E.PAN.PAGE_LOADED]: def('PAN_STAGE', 'pan', PEM),
    [E.PAN.PROCEED]: def('PAN_STAGE', 'pan', PEM),
    [E.PAN.VERIFICATION_COMPLETED]: def('PAN_STAGE', 'PAN Page', PEM),

    // 11–13 PAN_Verified
    [E.PAN_VERIFIED.PAGE_LOADED]: def('PAN_Verified', 'pan_verified', PEM),
    // client_name is a user attribute only (handover §4) — not on the event.
    [E.PAN_VERIFIED.PROCEED]: def('PAN_Verified', 'pan_verified', PEM),
    [E.PAN_VERIFIED.CDP]: def('PAN_Verified', 'pan_verified'),

    // 14–15 DIGILOCKER
    [E.DIGILOCKER.INITIATED]: def('DIGILOCKER', 'digilocker', PEM),
    [E.DIGILOCKER.SUCCESS]: def('DIGILOCKER', 'digilocker', PEM),

    // 16–19 BANK
    [E.BANK.PENNYDROP_INITIATED]: def('BANK', 'bank', BANK_REQUIRED, BANK_OPTIONAL, BANK_ENUMS),
    [E.BANK.PENNYDROP_SUCCESS]: def('BANK', 'bank', BANK_REQUIRED, BANK_OPTIONAL, BANK_ENUMS),
    [E.BANK.RPD_INITIATED]: def('BANK', 'bank', BANK_REQUIRED, BANK_OPTIONAL, BANK_ENUMS),
    [E.BANK.RPD_SUCCESS]: def('BANK', 'bank', BANK_REQUIRED, BANK_OPTIONAL, BANK_ENUMS),
    [E.BANK.VERIFICATION_COMPLETED]: def('BANK', 'Bank Page', PEM),

    // 20–22 Selfie
    [E.SELFIE.BANK_VERIFIED_CDP]: def('Selfie', 'selfie'),
    [E.SELFIE.SCREEN_LOADED]: def('Selfie', 'selfie', PEM),
    [E.SELFIE.SCREEN_PROCEED]: def('Selfie', 'selfie', PEM),
    [E.SELFIE.LOCATION_PERMISSION]: def('Selfie', 'Photo Page',
        { mobile_number: S, location_permission_status: S, page_name: S }, {}, { location_permission_status: ['allowed'] }),
    [E.SELFIE.LOCATION_DENIED]: def('Selfie', 'Photo Page',
        { mobile_number: S, location_permission_status: S, page_name: S }, {}, { location_permission_status: ['denied'] }),

    // 23–25 SIGNATURE
    [E.SIGNATURE.DOCUMENT_UPLOAD_COMPLETED]: def('SIGNATURE', 'signature'),
    [E.SIGNATURE.LOADED]: def('SIGNATURE', 'signature', PEM),
    [E.SIGNATURE.PROCEED]: def('SIGNATURE', 'signature', PEM),

    // 26–32 Other Details
    [E.OTHER_DETAILS.LOADED]: def('Other Details', 'other_details', PEM),
    [E.OTHER_DETAILS.PROCEED]: def('Other Details', 'Other Details Page',
        { mobile_number: S, 'FNO opted': S, page_name: S }, {}, { 'FNO opted': ['FNO opted in', 'FNO opted out'] }),
    [E.OTHER_DETAILS.INCOME_PROOF_INITIATED]: def('Other Details', 'other_details', PEM),
    [E.OTHER_DETAILS.INCOME_PROOF_SUCCESS]: def('Other Details', 'other_details', PEM),
    [E.OTHER_DETAILS.INCOME_PROOF_VIEWED]: def('Other Details', 'Other Details Page', PEM),
    [E.OTHER_DETAILS.INCOME_PROOF_NOT_VISITED]: def('Other Details', 'Other Details Page', PEM),
    [E.OTHER_DETAILS.NOMINEE_ADDED]: def('Other Details', 'Nominee Page', PEM),
    [E.OTHER_DETAILS.INCOME_ACCOUNT_AGGREGATOR]: def('Other Details', 'INCOME',
        { ...PEM, account_aggregator: S }, {}, { account_aggregator: ['Success', 'Failed'] }),
    [E.OTHER_DETAILS.FNO_SELECTED]: def('Other Details', 'other_details'),

    // 33 AOF
    [E.AOF.GENERATED_SUCCESS]: def('AOF', 'aof', PEM, { aof_version: S }),

    // 34–36 ESIGN
    [E.ESIGN.COMPLETED_SUCCESS]: def('ESIGN', 'esign', ESIGN_REQUIRED),
    [E.ESIGN.DONE]: def('ESIGN', 'esign', PEM),
    [E.ESIGN.COMPLETED_FAILED]: def('ESIGN', 'esign', ESIGN_REQUIRED),

    // 37–39 ACCOUNT_CREATION
    [E.ACCOUNT_CREATION.SUCCESS]: def('ACCOUNT_CREATION', 'congratulation',
        { page_name: S, Client_Code: S, mobile_number: S }),
    [E.ACCOUNT_CREATION.ADD_FUNDS]: def('ACCOUNT_CREATION', 'congratulation'),
    [E.ACCOUNT_CREATION.FUNDS_ADDED]: def('ACCOUNT_CREATION', 'congratulation'),

    // 40 ACCOUNT_UNDER_VERIFICATION
    [E.ACCOUNT_UNDER_VERIFICATION.LOADED]: def('ACCOUNT_UNDER_VERIFICATION', 'non_stp', PEM),

    // Rejection review (/dad/rejectionCorrection). session_type has no web source yet
    // (open question) — debug builds report it missing via the handover check.
    [E.REJECTION.PAGE_LOADED]: def('REJECTION', 'rejection_review', { mobile_number: S, total_rejected_items: N }),
    [E.REJECTION.PAGE_VIEWED]: def('REJECTION', 'rejection_review', { mobile_number: S, total_rejected_items: N }),
    [E.REJECTION.PROCEED]: def('REJECTION', 'rejection_review', { mobile_number: S }),
    [E.REJECTION.FSN_OPENED]: def('REJECTION', 'rejection_review', { mobile_number: S }),
    [E.REJECTION.FSN_SUBMITTED]: def('REJECTION', 'rejection_review', { mobile_number: S, relation: S, relation_changed: S }),
    [E.REJECTION.FSN_ERROR]: def('REJECTION', 'rejection_review', { mobile_number: S, relation: S, relation_changed: S, code: S }),
    [E.REJECTION.DIGILOCKER_REDO_INITIATED]: def('REJECTION', 'rejection_review', { mobile_number: S }),
    [E.REJECTION.SELFIE_REDO_INITIATED]: def('REJECTION', 'rejection_review', { mobile_number: S }),

    // Global journey events (common props only). DROPPED is not sent by the frontend.
    [E.JOURNEY.WEB_SESSION_INIT]: def('JOURNEY'),
    [E.JOURNEY.STARTED]: def('JOURNEY'),
    [E.JOURNEY.RESUMED]: def('JOURNEY'),
    [E.JOURNEY.DROPPED]: def('JOURNEY'),
    [E.JOURNEY.COMPLETED]: def('JOURNEY'),
});

/** @param {string} name */
export const getSchema = (name) => EVENT_SCHEMA[name] || null;

/**
 * Dev-only validation. Returns a list of problems; never throws.
 * @param {string} name
 * @param {object|null} schema
 * @param {Record<string, unknown>} props  merged payload (after empty values are dropped)
 * @returns {string[]}
 */
export const validateEvent = (name, schema, props) => {
    if (!schema) return [`unknown event "${name}" — add it to events.js and eventSchema.js`];
    const problems = [];
    const typeOk = (key, type) => typeof props[key] === type;

    Object.keys(schema.required).forEach((key) => {
        if (!(key in props)) problems.push(`missing required "${key}"`);
        else if (!typeOk(key, schema.required[key])) {
            problems.push(`"${key}" should be ${schema.required[key]}, got ${typeof props[key]}`);
        }
    });
    Object.keys(schema.optional).forEach((key) => {
        if (key in props && !typeOk(key, schema.optional[key])) {
            problems.push(`"${key}" should be ${schema.optional[key]}, got ${typeof props[key]}`);
        }
    });
    Object.keys(schema.enums).forEach((key) => {
        if (key in props && !schema.enums[key].includes(props[key])) {
            problems.push(`"${key}" must be one of ${schema.enums[key].join(' | ')}`);
        }
    });
    return problems;
};
